import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import { SearchAndFilterBar } from '../../components/search-and-filter-bar';
import ActiveFilters from '../users-page/components/ActiveFilters';
import CarriersTable from './components/CarriersTable';
import ResponseModal from '../../components/modals/response-modal';
import FilesViewerModal from '../../components/modals/files-viewer/FilesViewerModal';
import EmptyState from '../../components/not-result';
import SEOHead from '../../components/SEO/SEOHead';
import carrierService from '../../services/carrierService';
import AddForm from '../../components/modals/add-item';
import { getUserRole, canEditCarriers, canCreateCarriers } from '../../utils/roleAccess';

const ITEMS_PER_PAGE = 10;

const normalizeCarrier = (rawCarrier = {}) => {
  const carrier = rawCarrier.data || rawCarrier;
  
  const equipmentType = carrier.equipmentType || "";
  const size = carrier.size || "";
  const equipment = Array.isArray(carrier.equipment) && carrier.equipment.length > 0
    ? carrier.equipment
    : (() => {
        const types = Array.isArray(equipmentType) ? equipmentType : (equipmentType ? [equipmentType] : []);
        const sizes = Array.isArray(size) ? size : (size ? [size] : []);
        return types.map(type => ({ type, sizes }));
      })();

  return {
    _id: carrier._id || carrier.id,
    id: carrier._id || carrier.id,
    name: carrier.name || "",
    companyName: carrier.companyName || "",
    dba: carrier.dba || "",
    type: carrier.type || "driver",
    mcNumber: carrier.mcNumber || "",
    dotNumber: carrier.dotNumber || "",
    email: carrier.email || "",
    phoneNumber: carrier.phoneNumber || "",
    address: carrier.address || {},
    equipment,
    equipmentType,
    size,
    capabilities: carrier.capabilities || [],
    certifications: carrier.certifications || [],
    pdfs: carrier.pdfs || [],
    people: Array.isArray(carrier.people) ? carrier.people : [],
    status: carrier.status || "active",
    createdAt: carrier.createdAt ? new Date(carrier.createdAt) : new Date(),
    updatedAt: carrier.updatedAt ? new Date(carrier.updatedAt) : new Date(),
    expanded: false,
    city: carrier.address?.city || "",
    state: carrier.address?.state || "",
    zipCode: carrier.address?.zipCode || "",
    location: `${carrier.address?.city || ""}, ${carrier.address?.state || ""}`.trim().replace(/^,\s*|,\s*$/g, ''),
    mc: carrier.mcNumber || "",
    dot: carrier.dotNumber || "",
  };
};

const CarriersPage = () => {
  const { getThemeColors } = useTheme();
  const { user } = useUser();
  const colors = getThemeColors();
  const role = getUserRole(user);
  const canEdit = canEditCarriers(role);
  const canCreate = canCreateCarriers(role);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [carriers, setCarriers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCarrierData, setEditCarrierData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: '',
    status: 'success',
    title: ''
  });

  // Files viewer modal state
  const [filesModal, setFilesModal] = useState({
    isOpen: false,
    files: [],
    entityName: ''
  });

  const showResponseModal = (message, status = 'success', title = '') => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title
    });
  };

  const closeResponseModal = () => {
    setResponseModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleViewFiles = (carrier) => {
    setFilesModal({
      isOpen: true,
      files: carrier.pdfs || [],
      entityName: carrier.companyName || carrier.name
    });
  };

  const closeFilesModal = () => {
    setFilesModal({ isOpen: false, files: [], entityName: '' });
  };

  const buildFilterParams = (values = {}) => {
    const params = {};
    const setIf = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim() === '') return;
      params[key] = value;
    };

    setIf('name', values.name);
    setIf('companyName', values.companyName);
    setIf('email', values.email);
    setIf('phoneNumber', values.phoneNumber);
    setIf('mcNumber', values.mcNumber);
    setIf('dotNumber', values.dotNumber);
    setIf('status', values.status);
    setIf('equipmentType', values.equipmentType);
    setIf('size', values.size);
    setIf('capabilities', values.capabilities);
    setIf('certifications', values.certifications);
    setIf('orderId', values.orderId);
    setIf('type', values['people.type']);

    if (values.city) {
      params['address.city'] = values.city;
    }
    if (values.state) {
      params['address.state'] = values.state;
    }
    if (values['address.address']) {
      params['address.address'] = values['address.address'];
    }
    if (values.zipCode) {
      params.zipCode = values.zipCode;
    }
    if (values.createdAt || values.createdDate) {
      params.createdAt = values.createdAt || values.createdDate;
    }

    return params;
  };

  const fetchCarriers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      const filterParams = buildFilterParams(filterValues);
      const hasFilters = Object.keys(filterParams).length > 0;
      Object.assign(params, filterParams);

      let response;
      if (searchTerm.trim() || hasFilters) {
        const searchParams = {
          ...params,
          ...(searchTerm.trim() && { q: searchTerm.trim() }),
        };
        response = await carrierService.searchCarriers(searchParams);
      } else {
        response = await carrierService.getCarriers(params);
      }

      const apiResponse = response?.data || response;
      const rawCarriers = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const pagination = apiResponse?.pagination || apiResponse?.meta || {};

      if (!Array.isArray(rawCarriers) || rawCarriers.length === 0) {
        console.warn("No carriers found in API response");
        setCarriers([]);
        setTotalItems(0);
        return;
      }


      const normalizedCarriers = rawCarriers.map(normalizeCarrier);
      const total = pagination.total ?? pagination.totalItems ?? rawCarriers.length;
      const totalPages =
        pagination.totalPages ??
        (total > 0 ? Math.ceil(total / ITEMS_PER_PAGE) : 1);

      if (totalPages > 0 && currentPage > totalPages) {
        setCurrentPage(totalPages);
        return;
      }

      setCarriers(normalizedCarriers);
      setTotalItems(total);
    } catch (err) {
      console.error("Error fetching carriers:", err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message;
      if (errorMessage === 'Invalid ID format') {
        setFetchError("No carriers found matching your search criteria");
      } else {
        setFetchError(errorMessage || "Failed to fetch carriers");
      }
      setCarriers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterValues]);

  const handleSearchSubmit = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCarriers();
  }, [currentPage, searchTerm, filterValues, fetchCarriers]);

  const handleFilterApply = (filters) => {
    setFilterValues(filters);
    
    // Convert filter object to array of filter strings for ActiveFilters display
    const filterArray = [];
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          filterArray.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'boolean' && value) {
          filterArray.push(key);
        } else if (typeof value === 'string' && value.trim() !== '') {
          filterArray.push(`${key}: ${value}`);
        }
      }
    });
    setActiveFilters(filterArray);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterIndex) => {
    const filterToRemove = activeFilters[filterIndex];
    const newFilters = activeFilters.filter((_, index) => index !== filterIndex);
    setActiveFilters(newFilters);
    
    // Also update filterValues
    const updatedFilterValues = { ...filterValues };
    const key = filterToRemove.split(':')[0].trim();
    if (updatedFilterValues[key] !== undefined) {
      if (Array.isArray(updatedFilterValues[key])) {
        updatedFilterValues[key] = [];
      } else {
        updatedFilterValues[key] = '';
      }
      setFilterValues(updatedFilterValues);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilterValues({});
    setCurrentPage(1);
  };

  const handleSelectAll = (pageItemIds = []) => {
    if (pageItemIds.length === 0) {
      setSelectedCarriers([]);
      return;
    }

    const hasSelectedAll = pageItemIds.every((id) =>
      selectedCarriers.includes(id)
    );

    if (hasSelectedAll) {
      setSelectedCarriers((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedCarriers((prev) => {
        const merged = new Set(prev);
        pageItemIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    }
  };

  const handleSelectCarrier = (carrierId) => {
    setSelectedCarriers(prev => 
      prev.includes(carrierId) 
        ? prev.filter(id => id !== carrierId)
        : [...prev, carrierId]
    );
  };

  const handleDeleteSelected = async (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) {
      if (selectedCarriers.length === 0) return;
      idsToDelete = selectedCarriers;
    }

    try {
      setLoading(true);
      await Promise.all(
        idsToDelete.map((id) => carrierService.deleteCarrier(id))
      );
      
      showResponseModal(
        `Successfully deleted ${idsToDelete.length} carrier(s)`,
        'success',
        'Deleted'
      );
      await fetchCarriers();
      setSelectedCarriers([]);
    } catch (err) {
      console.error("Error deleting carriers:", err);
      showResponseModal(
        err?.response?.data?.error || err?.message || "Failed to delete carriers",
        'error',
        'Delete Failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (carrierId) => {
    setCarriers(prev => prev.map(carrier => 
      (carrier._id || carrier.id) === carrierId 
        ? { ...carrier, expanded: !carrier.expanded }
        : carrier
    ));
  };


  return (
    <>
      <SEOHead
        title="Carrier Management"
        description="Manage carrier relationships and information. Track carrier details, equipment types, certifications, MC numbers, DOT numbers, and performance metrics."
        keywords="carrier management, freight carriers, transportation carriers, logistics carriers, carrier tracking, MC number, DOT number, carrier database"
        url="/carriers"
      />
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <SearchAndFilterBar
        searchTerm={searchInputValue}
        onSearchChange={setSearchInputValue}
        onSearchSubmit={handleSearchSubmit}
        onFilterApply={handleFilterApply}
        initialFilters={filterValues}
        pageType="carriers"
        showAddButton={canCreate}
        onAddSuccess={() => {
          showResponseModal('Carrier created successfully', 'success', 'Success');
          fetchCarriers();
        }}
      />

      {activeFilters.length > 0 && (
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      {fetchError ? (
        <EmptyState
          variant="error"
          title="Failed to load data"
          message={fetchError}
          onRetry={fetchCarriers}
          retryText="Retry"
        />
      ) : carriers.length === 0 && !loading ? (
        <EmptyState
          variant={searchTerm || Object.keys(filterValues).length > 0 ? "search" : "empty"}
          title={searchTerm || Object.keys(filterValues).length > 0 ? "No carriers found" : "No carriers yet"}
          message={
            searchTerm || Object.keys(filterValues).length > 0
              ? searchTerm
                ? `No carriers found matching "${searchTerm}". Try adjusting your search or filters.`
                : "No carriers found matching your filters. Try adjusting your filter criteria."
              : "Add your first carrier to get started"
          }
        />
      ) : (
        <CarriersTable
          carriers={carriers}
          selectedCarriers={selectedCarriers}
          onSelectAll={handleSelectAll}
          onSelectCarrier={handleSelectCarrier}
          onToggleExpand={handleToggleExpand}
          onDeleteSelected={handleDeleteSelected}
          onEdit={(carrier) => {
            if (!canEdit) return;
            setEditCarrierData(carrier);
            setIsEditModalOpen(true);
          }}
          onViewFiles={handleViewFiles}
          canSelect={canEdit}
          canDelete={canEdit}
          canEdit={canEdit}
          canChangeStatus={canEdit}
          onChangeStatus={async (id, status) => {
            try {
              await carrierService.updateCarrier(id, { status });
              showResponseModal(
                `Carrier status updated to ${status}`,
                'success',
                'Status Updated'
              );
              await fetchCarriers();
            } catch (error) {
              showResponseModal(
                error?.response?.data?.error || error?.message || `Failed to update carrier status`,
                'error',
                'Update Failed'
              );
            }
          }}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          loading={loading}
        />
      )}


      {isEditModalOpen && editCarrierData && (
        <AddForm
            isOpen={isEditModalOpen}
            setIsOpen={setIsEditModalOpen}
            formType="carriers"
            userData={{
              companyName: editCarrierData.companyName,
              fullName: editCarrierData.name || editCarrierData.companyName,
              dba: editCarrierData.dba || "",
              type: editCarrierData.type || 'driver',
              email: editCarrierData.email,
              message: editCarrierData.phoneNumber,
              mcNumber: editCarrierData.mcNumber,
              dotNumber: editCarrierData.dotNumber,
              carrierAddress: editCarrierData.address?.address || '',
              carrierCity: editCarrierData.address?.city || editCarrierData.city || '',
              carrierState: editCarrierData.address?.state || editCarrierData.state || '',
              carrierZipCode: editCarrierData.address?.zipCode || editCarrierData.zipCode || '',
              equipment: editCarrierData.equipment || [],
              equipmentType: editCarrierData.equipmentType || '',
              size: editCarrierData.size || '',
              capabilities: editCarrierData.capabilities || [],
              certifications: editCarrierData.certifications || [],
              routing: editCarrierData.routing || '',
              bankAccount: editCarrierData.bankAccount || '',
              accountNumber: editCarrierData.accountNumber || '',
              pdfs: editCarrierData.pdfs || [],
              people: Array.isArray(editCarrierData.people) ? editCarrierData.people : []
            }}
            isEditMode={true}
            itemId={editCarrierData._id || editCarrierData.id}
            onSuccess={() => {
              showResponseModal('Carrier updated successfully', 'success', 'Success');
              fetchCarriers();
              setIsEditModalOpen(false);
              setEditCarrierData(null);
            }}
            onClose={() => {
              setIsEditModalOpen(false);
              setEditCarrierData(null);
            }}
          />
      )}

      <ResponseModal
        isOpen={responseModal.isOpen}
        message={responseModal.message}
        status={responseModal.status}
        title={responseModal.title}
        onClose={closeResponseModal}
      />

      <FilesViewerModal
        isOpen={filesModal.isOpen}
        onClose={closeFilesModal}
        files={filesModal.files}
        title="Carrier Documents"
        entityName={filesModal.entityName}
      />
    </Container>
    </>
  );
};

export default CarriersPage;
